import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, MapPin, Users, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Link } from "react-router-dom";

interface Event {
  id: string;
  title: string;
  slug: string;
  date: string;
  location: string;
  event_type: string;
  max_participants?: number;
  current_participants: number;
  status: string;
  image_url?: string;
}

interface ListingEventsProps {
  events: Event[];
}

export const ListingEvents = ({ events }: ListingEventsProps) => {
  const upcomingEvents = events.filter(
    e => e.status === 'approved' && new Date(e.date) > new Date()
  ).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (upcomingEvents.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Próximos Eventos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {upcomingEvents.slice(0, 5).map((event) => (
            <Link key={event.id} to={`/event/${event.slug}`}>
              <div className="p-4 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer">
                <div className="flex gap-4">
                  {event.image_url && (
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="font-semibold line-clamp-1">{event.title}</h4>
                      <Badge variant="secondary">{event.event_type}</Badge>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(event.date), "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es })}
                      </p>
                      <p className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </p>
                      {event.max_participants && (
                        <p className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {event.current_participants} / {event.max_participants} participantes
                        </p>
                      )}
                    </div>
                    <Button size="sm" variant="link" className="p-0 h-auto mt-2">
                      Ver detalles
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
